import Big from 'big.js';
import { DateTime } from 'luxon';
import { AxiosStatusCode } from 'smg/axios';
import * as buttons from 'smg/buttons';
import * as lineitems from 'smg/customer-api/lineitems';
import { GetLineItems } from 'smg/customer-api/v2/lineitems';
import * as models from 'smg/customer-api/models';
import { OpenQuotes } from 'smg/customer-api/v2/quotes';
import { SetDocumentTitle } from 'smg/dom';
import { HumanFileSize } from 'smg/formatting';
import * as html from 'smg/html';
import { Linkify } from 'smg/linkify';
import { IsImageURL, Picture } from 'smg/picture';
import QueryString from 'smg/querystring';
import { SanitizeRichHTML } from 'smg/richtext';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import * as carts from '../carts';

let $spinner: HTMLElement;
let $error: HTMLElement;
let $errorMessage: HTMLElement;
let $content: HTMLElement;
let $title: HTMLElement;
let $quotesList: HTMLElement;
let $notFound: HTMLElement;
let $searchParent: HTMLElement;
let $backToSearch: HTMLElement;

void main();

async function main() {
    initElements();

    const qs = QueryString();
    const quoteKey = qs.get('id') || qs.get('key') || qs.get('quote');
    const instance = qs.get('instance');
    const searchQuery = qs.get('q');

    if (quoteKey) {
        await loadSingleQuote(quoteKey);
    } else if (instance) {
        // Quote sent instance — search by key or ID depending on format
        const q = instance.length === 43 ? `quoteSentKey:${instance}` : `quoteSentInstanceID:${instance}`;
        await searchQuotes(q);
    } else if (searchQuery) {
        await searchQuotes(searchQuery);
    } else {
        showSearch();
    }
}

function initElements() {
    $spinner = document.getElementById('quotes-spinner') as HTMLElement;
    $error = document.getElementById('quotes-error') as HTMLElement;
    $errorMessage = document.getElementById('quotes-error-message') as HTMLElement;
    $content = document.getElementById('quotes-content') as HTMLElement;
    $title = document.getElementById('quotes-title') as HTMLElement;
    $quotesList = document.getElementById('quotes-list') as HTMLElement;
    $notFound = document.getElementById('quote-not-found') as HTMLElement;
    $searchParent = document.getElementById('quote-search-parent') as HTMLElement;
    $backToSearch = document.getElementById('back-to-search') as HTMLElement;
}

function showSearch() {
    hideSpinner();
    $searchParent.classList.remove('d-none');
}

async function loadSingleQuote(key: string) {
    try {
        const data = await OpenQuotes({
            data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteRequest,key,quoteSents',
            q: `key:${key}`,
            max: 1,
        });

        if (data.results.length === 0) {
            showNotFound();
            return;
        }

        const quote = data.results[0];
        const quoteTitle = `Quote #${quote.serial ?? ''}`;
        SetDocumentTitle(quoteTitle);
        $title.textContent = quoteTitle;

        await drawQuotes([quote]);

        hideSpinner();
        $content.classList.remove('d-none');
    } catch (err) {
        if (AxiosStatusCode(err) === 404) {
            showNotFound();
        } else {
            showError(err);
        }
    }
}

async function searchQuotes(q: string) {
    try {
        const data = await OpenQuotes({
            data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteRequest,key,quoteSents',
            sort: 'created desc',
            q,
        });

        const quotes = data.results;

        if (quotes.length === 0) {
            hideSpinner();
            $searchParent.classList.remove('d-none');
            const $warning = document.getElementById('empty-search-warning') as HTMLElement;
            $warning.classList.remove('d-none');
            const $searchInput = document.getElementById('quote-search') as HTMLInputElement;
            $searchInput.value = q;
            return;
        }

        $title.textContent = 'Your Quotes';
        $backToSearch.classList.remove('d-none');

        await drawQuotes(quotes);

        hideSpinner();
        $content.classList.remove('d-none');
    } catch (err) {
        showError(err);
    }
}

// The most recent quote-sent for one quote — the message a sales rep writes
// when sending a quote. Sends with missing/unparseable dates lose to any
// dated send.
function latestQuoteSent(quote: models.Quote): models.QuoteSent | undefined {
    let latest: models.QuoteSent | undefined;
    for (const quoteSent of quote.quoteSents) {
        if (!latest || isNewerQuoteSent(quoteSent, latest)) {
            latest = quoteSent;
        }
    }
    return latest;
}

function isNewerQuoteSent(a: models.QuoteSent, b: models.QuoteSent): boolean {
    if (!a.created?.isValid) {
        return false;
    }
    if (!b.created?.isValid) {
        return true;
    }
    return a.created.toMillis() > b.created.toMillis();
}

function quoteSentMillis(quoteSent: models.QuoteSent): number {
    if (quoteSent.created?.isValid) {
        return quoteSent.created.toMillis();
    }
    return -Infinity;
}

interface quoteSendGroup {
    quoteSent: models.QuoteSent;
    quotes: models.Quote[];
}

// Groups quotes by the send instance of each quote's most recent quote-sent
// (very old sends have no instance ID, so fall back to the send's own ID).
// Groups sort by their send's created desc; quotes keep API order within a
// group. Quotes with no sends at all come back separately and render last.
function groupQuotesBySend(quotes: models.Quote[]): { groups: quoteSendGroup[]; unsent: models.Quote[] } {
    const groupsByKey = new Map<string, quoteSendGroup>();
    const unsent: models.Quote[] = [];

    for (const quote of quotes) {
        const quoteSent = latestQuoteSent(quote);
        if (!quoteSent) {
            unsent.push(quote);
            continue;
        }

        const key = quoteSent.quoteSentInstanceID || quoteSent.id || quoteSent.key || quote.key;
        let group = groupsByKey.get(key);
        if (!group) {
            group = { quoteSent, quotes: [] };
            groupsByKey.set(key, group);
        }
        group.quotes.push(quote);
        if (isNewerQuoteSent(quoteSent, group.quoteSent)) {
            group.quoteSent = quoteSent;
        }
    }

    const groups = [...groupsByKey.values()];
    groups.sort((a, b) => quoteSentMillis(b.quoteSent) - quoteSentMillis(a.quoteSent));
    return { groups, unsent };
}

// Draws every quote grouped by send instance: each group's message card above
// that group's quote cards. Every older group is announced by a dated boundary
// marker so the sections can't run together — the newest group needs no marker
// (its message card is the header). Unsent quotes render last, behind their
// own marker.
async function drawQuotes(quotes: models.Quote[]) {
    const { groups, unsent } = groupQuotesBySend(quotes);

    for (const [index, group] of groups.entries()) {
        if (index > 0) {
            $quotesList.appendChild(drawSendSeparator(group.quoteSent));
        }
        drawQuoteMessage(group.quoteSent, index > 0);
        for (const quote of group.quotes) {
            const $card = await drawQuoteCard(quote);
            $quotesList.appendChild($card);
        }
    }

    if (unsent.length) {
        if (groups.length) {
            $quotesList.appendChild(drawSendSeparator());
        }
        for (const quote of unsent) {
            const $card = await drawQuoteCard(quote);
            $quotesList.appendChild($card);
        }
    }
}

// Boundary marker between send groups. Carries the group's send date so each
// older section is clearly its own moment in the thread; without a quoteSent
// (the unsent tail) the marker still separates, just without a date.
function drawSendSeparator(quoteSent?: models.QuoteSent): HTMLElement {
    const $separator = template.Clone('#quote-send-separator-template').firstElementChild as HTMLElement;

    const $label = $separator.querySelector('.quote-send-separator-date') as HTMLElement;
    if (quoteSent?.created?.isValid) {
        $label.textContent = `Sent ${quoteSent.created.toLocaleString(DateTime.DATE_MED)}`;
    } else if (quoteSent) {
        $label.textContent = 'Earlier quotes';
    } else {
        $separator.querySelector('.quote-send-separator-icon')?.remove();
        $label.textContent = 'Not yet sent';
    }

    return $separator;
}

// Draws one send's message card above its group's quote cards. Many sends are
// SMS-only, so without this the customer never sees the rep's note anywhere.
// A send with no body but with email attachments still gets a card — just the
// header plus the attachments. A send with neither renders nothing, leaving
// the group's quotes to speak for themselves. Older sends render subtler than
// the newest one.
function drawQuoteMessage(quoteSent: models.QuoteSent, subtle: boolean) {
    const content = quoteSent.content?.trim() ?? '';
    const subject = quoteSent.subject?.trim() ?? '';
    const attachments = quoteSent.attachments.filter(attachment => attachment.publicURL);
    // Subject-only sends still render a header card — the subject often
    // carries the send's intent (e.g. preliminary vs action needed).
    if (!content && !subject && !attachments.length) {
        return;
    }

    const $card = template.Clone('#quote-message-template').firstElementChild as HTMLElement;

    const firstName = quoteSent.user?.firstName?.trim();
    const $sender = $card.querySelector('.quote-message-sender') as HTMLElement;
    $sender.textContent = firstName ? `A message from ${firstName}` : 'A message from our team';

    const $subject = $card.querySelector('.quote-message-subject') as HTMLElement;
    $subject.textContent = quoteSent.subject?.trim() || 'Your Quote Is Ready';

    const $date = $card.querySelector('.quote-message-date') as HTMLElement;
    if (quoteSent.created?.isValid) {
        $date.textContent = quoteSent.created.toLocaleString(DateTime.DATETIME_MED);
    } else {
        $date.remove();
    }

    const $body = $card.querySelector('.quote-message-body') as HTMLElement;
    if (content) {
        if (quoteSent.contentType === 'text/html') {
            $body.innerHTML = Linkify(SanitizeRichHTML(content));
        } else {
            $body.innerHTML = Linkify(html.Nl2br(html.Escape(content)));
        }
    } else {
        $body.remove();
    }

    if (attachments.length) {
        drawQuoteSentAttachments($card, quoteSent, attachments);
    }

    if (subtle) {
        $card.style.boxShadow = 'none';
        $card.classList.add('opacity-75');
    }

    $quotesList.appendChild($card);
}

function isImageAttachment(attachment: models.QuoteSentAttachment): boolean {
    return IsImageURL(attachment.name || attachment.publicURL);
}

// The rep's email attachments ride along with the send's message card: images
// as thumbnails that open into the send's own fancybox gallery, other files as
// compact chips that open in a new tab.
function drawQuoteSentAttachments($card: HTMLElement, quoteSent: models.QuoteSent, attachments: models.QuoteSentAttachment[]) {
    const $section = $card.querySelector('.quote-attachments') as HTMLElement;
    $section.classList.remove('d-none');

    const fancyboxGroup = `quote-sent-${quoteSent.quoteSentInstanceID || quoteSent.id || quoteSent.key}`;

    const $images = $section.querySelector('.quote-attachment-images') as HTMLElement;
    const $files = $section.querySelector('.quote-attachment-files') as HTMLElement;

    for (const attachment of attachments) {
        const name = attachment.name.trim() || 'Attachment';
        const publicURL = attachment.publicURL;

        if (isImageAttachment(attachment)) {
            const $link = template.Clone('#quote-attachment-image-template').firstElementChild as HTMLAnchorElement;
            $link.href = publicURL;
            $link.setAttribute('data-fancybox', fancyboxGroup);
            $link.setAttribute('data-caption', name);
            const $img = $link.querySelector('img') as HTMLImageElement;
            $img.alt = name;
            $images.appendChild($link);
            // Picture's fancyboxThumb finds the anchor through the img's
            // ancestors, so data-fancybox must already be set (done above).
            void Picture($img, publicURL, { displayWidth: 80, fancyboxThumb: true });
        } else {
            const $chip = template.Clone('#quote-attachment-file-template').firstElementChild as HTMLAnchorElement;
            $chip.href = publicURL;
            const $name = $chip.querySelector('.quote-attachment-file-name') as HTMLElement;
            $name.textContent = name;
            const $size = $chip.querySelector('.quote-attachment-file-size') as HTMLElement;
            $size.textContent = attachment.size ? HumanFileSize(attachment.size, true) : '';
            $files.appendChild($chip);
        }
    }

    if (!$images.hasChildNodes()) {
        $images.remove();
    }
    if (!$files.hasChildNodes()) {
        $files.remove();
    }
}

async function drawQuoteCard(quote: models.Quote): Promise<HTMLElement> {
    const $card = template.Clone('#quote-card-template').firstElementChild as HTMLElement;

    const $serial = $card.querySelector('.quote-serial') as HTMLElement;
    $serial.textContent = `Quote #${quote.serial ?? ''}`;

    const $date = $card.querySelector('.quote-date') as HTMLElement;
    $date.textContent = `Submitted: ${quote.created?.isValid ? quote.created.toLocaleString() : 'N/A'}`;

    const $total = $card.querySelector('.quote-total') as HTMLElement;
    $total.textContent = DollarFormat(quote.cachedTotal ?? 0);

    const $products = $card.querySelector('.quote-products') as HTMLElement;
    if (quote.quoteLineItems.length) {
        const lineItems = await GetLineItems({ data: 'name,pluralName,product' }).catch(() => new Map<string, models.LineItem>());
        const summaryMap = new Map<string, { qty: number; singular: string; plural: string }>();
        for (const li of quote.quoteLineItems) {
            const lineItem = lineItems.get(li.lineItemID ?? '');
            const singular = li.lineItem || lineItem?.name || li.lineItemModel?.name || 'Product';
            let entry = summaryMap.get(singular);
            if (!entry) {
                const plural = lineItem?.pluralName || `${singular}s`;
                entry = { qty: 0, singular, plural };
                summaryMap.set(singular, entry);
            }
            entry.qty += li.quantity || 0;
        }
        const parts: string[] = [];
        for (const s of summaryMap.values()) {
            parts.push(`${s.qty.toLocaleString()} ${s.qty !== 1 ? s.plural : s.singular}`);
        }
        $products.textContent = parts.join(', ');
    }

    const fancyboxGroup = `quote-${quote.key}`;

    const proofsByID = new Map<string, models.QuoteProof>();
    for (const qp of quote.quoteProofs) {
        if (qp.id) {
            proofsByID.set(qp.id, qp);
        }
    }

    const usedProofIDs = new Set<string>();
    for (const li of quote.quoteLineItems) {
        if (li.quoteProofID) {
            usedProofIDs.add(li.quoteProofID);
        }
    }

    const $lineItemsContainer = $card.querySelector('.quote-line-items') as HTMLElement;
    await drawLineItems(quote, $lineItemsContainer, proofsByID, fancyboxGroup);

    const $proofsSection = $card.querySelector('.quote-proofs-section') as HTMLElement;
    const $proofsContainer = $card.querySelector('.quote-proofs') as HTMLElement;
    let drewAnyProofs = false;
    for (const qp of quote.quoteProofs) {
        if (!qp.proof?.file) {
            continue;
        }
        if (qp.id && usedProofIDs.has(qp.id)) {
            continue;
        }
        const urls = new models.Proof(qp.proof).getURLs();
        const $wrapper = template.Clone('#proof-thumb-template').firstElementChild as HTMLElement;
        const $thumb = $wrapper.querySelector('.proof-thumb-link') as HTMLAnchorElement;
        $thumb.href = urls.main;
        $thumb.setAttribute('data-fancybox', fancyboxGroup);
        $thumb.setAttribute('data-caption', qp.proof.name || `Proof ${qp.number ?? ''}`);
        const $img = $thumb.querySelector('img') as HTMLImageElement;
        $img.src = urls.thumbnail;
        $img.alt = qp.proof.name || `Proof ${qp.number ?? ''}`;
        const $number = $wrapper.querySelector('.proof-thumb-number') as HTMLElement;
        $number.textContent = `Proof #${qp.number ?? ''}`;
        $proofsContainer.appendChild($wrapper);
        drewAnyProofs = true;
    }
    if (drewAnyProofs) {
        $proofsSection.classList.remove('d-none');
        if (usedProofIDs.size) {
            const $proofsTitle = $card.querySelector('.quote-proofs-title') as HTMLElement;
            $proofsTitle.textContent = 'Additional Proofs';
        }
    }

    const $addToCart = $card.querySelector('.quote-add-to-cart') as HTMLButtonElement;
    $addToCart.addEventListener('click', () => {
        buttons.Start($addToCart);
        void carts.AddCartItem({ quoteID: quote.key }).then(added => {
            if (!added) {
                return;
            }
            $addToCart.innerHTML = '<i class="fas fa-check me-1"></i> Added to cart';
            $addToCart.disabled = true;
            $addToCart.style.opacity = '0.6';
            $addToCart.style.cursor = 'default';
        }).finally(() => {
            buttons.Stop($addToCart);
        });
    });

    return $card;
}

async function drawLineItems(quote: models.Quote, container: HTMLElement, proofsByID: Map<string, models.QuoteProof>, fancyboxGroup: string) {
    if (!quote.quoteLineItems.length) {
        const $p = document.createElement('p');
        $p.className = 'text-muted';
        $p.textContent = 'No line items';
        container.replaceChildren($p);
        return;
    }

    const fragment = document.createDocumentFragment();
    const lineItems = await GetLineItems({ data: 'name,pluralName,product' }).catch(() => new Map<string, models.LineItem>());

    for (const li of quote.quoteLineItems) {
        const $li = template.Clone('#quote-line-item-template').firstElementChild as HTMLElement;

        const lineItem = lineItems.get(li.lineItemID ?? '');
        const $name = $li.querySelector('.line-item-name') as HTMLElement;
        $name.textContent = li.lineItem || lineItem?.name || li.lineItemModel?.name || 'Product';

        const $type = $li.querySelector('.line-item-type') as HTMLElement;
        const $size = $li.querySelector('.line-item-size') as HTMLElement;
        $type.textContent = `Type: ${li.type || li.lineItemType?.name || 'Standard'}`;
        $size.textContent = `Size: ${li.size || li.lineItemTypeSize?.name || 'N/A'}`;

        const $qty = $li.querySelector('.line-item-quantity') as HTMLElement;
        $qty.textContent = `Qty: ${(li.quantity ?? 0).toLocaleString()}`;

        const $subtotal = $li.querySelector('.line-item-subtotal') as HTMLElement;
        const subtotal = Big(li.price || 0).mul(li.quantity || 0);
        $subtotal.textContent = DollarFormat(subtotal);

        if (li.description) {
            const $description = $li.querySelector('.line-item-description') as HTMLElement;
            $description.classList.remove('d-none');
            $description.textContent = li.description;
        }

        const quoteProof = li.quoteProof ?? (li.quoteProofID ? proofsByID.get(li.quoteProofID) : undefined);
        const proofFile = quoteProof?.proof?.file;
        if (quoteProof && proofFile) {
            const urls = new models.Proof(quoteProof.proof).getURLs();
            const $thumbContainer = $li.querySelector('.line-item-proof-thumb') as HTMLElement;
            const $link = $thumbContainer.querySelector('a') as HTMLAnchorElement;
            const $img = $thumbContainer.querySelector('img') as HTMLImageElement;
            $thumbContainer.classList.remove('d-none');
            $link.href = urls.main;
            $link.setAttribute('data-fancybox', fancyboxGroup);
            $link.setAttribute('data-caption', quoteProof.proof?.name || `Proof ${quoteProof.number ?? ''}`);
            $img.src = urls.thumbnail;
            $img.alt = quoteProof.proof?.name || `Proof ${quoteProof.number ?? ''}`;

            const $proofNumber = $li.querySelector('.line-item-proof-number') as HTMLElement;
            $proofNumber.classList.remove('d-none');
            const $proofNumberText = $li.querySelector('.line-item-proof-number-text') as HTMLElement;
            $proofNumberText.textContent = `Proof #${quoteProof.number ?? ''}`;
        }

        fragment.appendChild($li);

        // Display child line items (mold fees, setup charges, add-ons, etc.) after parent
        if (li.quoteChildLineItems?.length) {
            const [childMap, optionMap] = await Promise.all([lineitems.ChildMap(), lineitems.OptionMap()]);
            for (const child of li.quoteChildLineItems) {
                const $child = template.Clone('#quote-line-item-template').firstElementChild as HTMLElement;

                const catalogChild = childMap.get(child.childLineItemID ?? '');
                const catalogOption = optionMap.get(child.childLineItemOptionID ?? '');
                const childName = [
                    catalogChild?.networkName() || child.child,
                    catalogOption?.networkName() || child.option,
                    child.description,
                ].filter(Boolean).join(' - ') || 'Add-on';
                const $childName = $child.querySelector('.line-item-name') as HTMLElement;
                $childName.textContent = childName;
                $childName.classList.remove('fw-semibold');
                $childName.classList.add('fw-normal', 'text-muted', 'small');

                $child.classList.add('ps-4');

                const $details = $child.querySelector('.line-item-details') as HTMLElement;
                $details?.remove();

                const $childQty = $child.querySelector('.line-item-quantity') as HTMLElement;
                $childQty.textContent = `Qty: ${(child.quantity ?? 0).toLocaleString()}`;

                const $childSubtotal = $child.querySelector('.line-item-subtotal') as HTMLElement;
                const childSubtotal = Big(child.price || 0).mul(child.quantity || 0);
                $childSubtotal.textContent = DollarFormat(childSubtotal);

                fragment.appendChild($child);
            }
        }
    }

    container.replaceChildren(fragment);
}

function hideSpinner() {
    $spinner.classList.add('d-none');
}

function showError(err: unknown) {
    hideSpinner();
    $errorMessage.textContent = err instanceof Error ? err.message : 'Something went wrong while loading your quotes.';
    $error.classList.remove('d-none');
}

function showNotFound() {
    hideSpinner();
    $notFound.classList.remove('d-none');
}
